import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { RotateCcw, RotateCw } from "react-feather";
import { usePdfiumEngine } from "@embedpdf/engines/react";
import type {
  PdfRendererHandle,
  PdfRendererSource,
} from "../pdf-renderer/pdf-renderer";
import { PdfToolbar } from "../pdf-renderer/pdf-toolbar";
import type { PdfToolbarContext } from "../pdf-renderer/pdf-renderer";
import {
  classifyUpload,
  fileBaseName,
  fileToPdfBuffer,
} from "./file-to-pdf-buffer";
import {
  PdfPageRotation,
  normalizeRotation,
  rotatePdfPages,
} from "./rotate-pdf-pages";
import { documentFileName } from "./download-source";
import {
  BASE_SOURCE_ID,
  DEFAULT_DOC_TYPE_OPTIONS,
  DocumentEntry,
  buildEntries,
  buildRenderedIds,
  docTypeSelectOptionsFor,
  initialSelectedIds,
  isRenderedDocument,
  isUploadEntry,
  mergeOrderStartPage,
  needsDocType,
  normalizeDocTypeOptions,
  pageIndices,
  renumberEntries,
  renderedFirst,
  sourceToArrayBuffer,
  toPublicDocuments,
} from "./merge-documents";
import type {
  PdfMergePanelProps,
  PdfMergePanelStep,
  PdfMergeResult,
} from "./types";

type MergeEngine = NonNullable<ReturnType<typeof usePdfiumEngine>["engine"]>;
type OpenedDocument = Awaited<
  ReturnType<ReturnType<MergeEngine["openDocumentBuffer"]>["toPromise"]>
>;

export type UsePdfMergeOptions = Pick<
  PdfMergePanelProps,
  | "src"
  | "documents"
  | "onDocumentsChange"
  | "onSelectionChange"
  | "onProceed"
  | "onAddDocument"
  | "onMergeError"
  | "onStepChange"
  | "onMergedTitleChange"
  | "defaultMergedTitle"
  | "downloadFileName"
  | "uploadedDocName"
  | "uploadedDocType"
  | "docTypeOptions"
  | "previewDisabled"
  | "autoMergeOnUpload"
  | "defaultAllSelected"
  | "showRotate"
  | "pdfRendererProps"
>;

export function usePdfMerge(options: UsePdfMergeOptions): UsePdfMergeReturn {
  return usePdfMergeImpl(options);
}

function usePdfMergeImpl({
  src,
  documents,
  onDocumentsChange,
  onSelectionChange,
  onProceed,
  onAddDocument,
  onMergeError,
  onStepChange,
  onMergedTitleChange,
  defaultMergedTitle = "",
  downloadFileName = "merged-document.pdf",
  uploadedDocName,
  uploadedDocType,
  docTypeOptions,
  previewDisabled = false,
  autoMergeOnUpload = true,
  defaultAllSelected = true,
  showRotate = true,
  pdfRendererProps,
}: UsePdfMergeOptions) {
  const [entries, setEntries] = useState(() => buildEntries(documents));
  const [selectedIds, setSelectedIds] = useState(() =>
    initialSelectedIds(buildEntries(documents), defaultAllSelected)
  );
  const [activeId, setActiveId] = useState(
    () => buildEntries(documents)[0]?.id ?? ""
  );
  const [step, setStep] = useState<PdfMergePanelStep>("select");
  const [mergedTitle, setMergedTitle] = useState(defaultMergedTitle);
  const [pendingIds, setPendingIds] = useState(() => new Set<string>());
  const [previewUploadIds, setPreviewUploadIds] = useState(
    () => new Set<string>()
  );
  const [renderedPdf, setRenderedPdf] = useState<ArrayBuffer | null>(null);
  const [renderVersion, setRenderVersion] = useState(0);
  const renderVersionRef = useRef(0);
  const [isMerging, setIsMerging] = useState(false);
  const mergeRequestRef = useRef(0);
  const activeMergeRequestRef = useRef<number | null>(null);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [autoMergeRequest, setAutoMergeRequest] = useState(0);
  const handledAutoMergeRef = useRef(0);
  const [reorderRequest, setReorderRequest] = useState(0);
  const handledReorderRef = useRef(0);
  const draggedIdRef = useRef<string | null>(null);
  const focusEntryAfterMergeRef = useRef<string | null>(null);
  const pendingViewerPageRef = useRef<number | null>(null);
  const viewerLayoutReadyRef = useRef(false);
  const [viewerNavigatePage, setViewerNavigatePage] = useState<number>();
  const [mergeFocusPage, setMergeFocusPage] = useState<number>();
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const titleFieldId = `${useId()}-merged-title`;
  const pdfRef = useRef<PdfRendererHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadCounterRef = useRef(0);
  const sourceBuffersRef = useRef(new Map<string, ArrayBuffer>());
  const uploadFilesRef = useRef(new Map<string, File>());
  const reportedStepRef = useRef(step);

  const {
    engine,
    isLoading: engineLoading,
    error: engineError,
  } = usePdfiumEngine({
    ...(pdfRendererProps?.wasmUrl
      ? {
          wasmUrl:
            typeof window !== "undefined"
              ? new URL(pdfRendererProps.wasmUrl, window.location.href).href
              : pdfRendererProps.wasmUrl,
        }
      : {}),
    ...(pdfRendererProps?.worker != null
      ? { worker: pdfRendererProps.worker }
      : {}),
  });

  const isPreviewStep = step === "preview";

  useEffect(() => {
    if (reportedStepRef.current === step) return;
    reportedStepRef.current = step;
    onStepChange?.(step);
  }, [step, onStepChange]);

  useEffect(() => {
    const nextEntries = buildEntries(documents);
    setEntries(nextEntries);
    setSelectedIds(initialSelectedIds(nextEntries, defaultAllSelected));
    setActiveId(nextEntries[0]?.id ?? "");
    setPreviewUploadIds(new Set());
  }, [documents, defaultAllSelected]);

  useEffect(() => {
    mergeRequestRef.current += 1;
    activeMergeRequestRef.current = null;
    sourceBuffersRef.current = new Map();
    uploadFilesRef.current = new Map();
    setRenderedPdf(null);
    setPendingIds(new Set());
    setPreviewUploadIds(new Set());
    setMergeError(null);
    setIsMerging(false);
    setStep("select");
    return () => {
      mergeRequestRef.current += 1;
      activeMergeRequestRef.current = null;
    };
  }, [src]);

  useEffect(() => {
    rowRefs.current.get(activeId)?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }, [activeId]);

  const showRendered = (pdf: ArrayBuffer) => {
    const version = ++renderVersionRef.current;
    setRenderedPdf(pdf);
    setRenderVersion(version);
    return version;
  };

  const activeSrc: PdfRendererSource = useMemo(
    () =>
      renderedPdf
        ? { buffer: renderedPdf, name: `merged-${renderVersion}.pdf` }
        : src,
    [renderedPdf, renderVersion, src]
  );

  const selectedDocuments = useMemo(
    () =>
      entries
        .filter((entry) => selectedIds.has(entry.id))
        .map((entry) => entry.document),
    [entries, selectedIds]
  );

  const docTypeChoices = useMemo(
    () =>
      normalizeDocTypeOptions(
        docTypeOptions ?? [
          ...documents.map(({ doc_type }) => doc_type),
          ...DEFAULT_DOC_TYPE_OPTIONS,
        ]
      ),
    [docTypeOptions, documents]
  );

  const clearPendingViewerNavigation = () => {
    pendingViewerPageRef.current = null;
    setViewerNavigatePage(undefined);
    setMergeFocusPage(undefined);
  };

  const flushViewerNavigation = (currentPage: number) => {
    const pending = pendingViewerPageRef.current;
    if (pending == null || !viewerLayoutReadyRef.current) return;
    if (currentPage !== pending) return;
    requestAnimationFrame(() => {
      const state = pdfRef.current?.getState();
      if (
        pendingViewerPageRef.current === pending &&
        state?.currentPage === pending
      ) {
        clearPendingViewerNavigation();
      }
    });
  };

  const applyPendingViewerNavigation = () => {
    const target = pendingViewerPageRef.current;
    if (target == null) return;
    pdfRef.current?.goToPage(target);
  };

  const navigateViewerToPage = (page: number) => {
    pendingViewerPageRef.current = page;
    setViewerNavigatePage(page);
    if (viewerLayoutReadyRef.current) {
      applyPendingViewerNavigation();
    }
  };

  const activateDocument = (entry: DocumentEntry) => {
    setActiveId(entry.id);
    if (isRenderedDocument(entry.document)) {
      navigateViewerToPage(entry.document.start_page);
    }
  };

  const toggleDocument = (entry: DocumentEntry) => {
    if (needsDocType(entry)) return;

    const nextIds = new Set(selectedIds);
    const isChecking = !nextIds.has(entry.id);
    if (isChecking) nextIds.add(entry.id);
    else nextIds.delete(entry.id);
    setSelectedIds(nextIds);
    onSelectionChange?.(
      entries
        .filter((item) => nextIds.has(item.id))
        .map((item) => item.document)
    );
  };

  const setDocType = (entry: DocumentEntry, docType: string) => {
    const typed = entries.map((item) =>
      item.id === entry.id
        ? { ...item, document: { ...item.document, doc_type: docType } }
        : item
    );
    setEntries(typed);
    onDocumentsChange?.(typed.map((item) => item.document));

    const nextSelected = new Set(selectedIds);
    nextSelected.add(entry.id);
    setSelectedIds(nextSelected);
    onSelectionChange?.(
      typed
        .filter((item) => nextSelected.has(item.id))
        .map((item) => item.document)
    );
    setPreviewUploadIds((current) => {
      const next = new Set(current);
      next.delete(entry.id);
      return next;
    });
  };

  const moveDocument = (sourceId: string, targetId: string) => {
    const sourceIndex = entries.findIndex((entry) => entry.id === sourceId);
    const targetIndex = entries.findIndex((entry) => entry.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex)
      return;

    const next = [...entries];
    const [moved] = next.splice(sourceIndex, 1);
    if (!moved) return;
    next.splice(targetIndex, 0, moved);

    const reordered = next.map((entry, index) => ({
      ...entry,
      document: { ...entry.document, merge_order: index + 1 },
    }));
    setEntries(reordered);
    onDocumentsChange?.(reordered.map((entry) => entry.document));
    if (isPreviewStep) setReorderRequest((current) => current + 1);
  };

  const rotateDocument = async (entry: DocumentEntry, degrees = 90) => {
    const rotated = entries.map((item) =>
      item.id === entry.id
        ? {
            ...item,
            document: {
              ...item.document,
              rotation: normalizeRotation(
                (item.document.rotation ?? 0) + degrees
              ),
            },
          }
        : item
    );
    setEntries(rotated);
    onDocumentsChange?.(rotated.map((item) => item.document));

    if (!isRenderedDocument(entry.document)) return;

    const turn: PdfPageRotation = {
      startPage: entry.document.start_page,
      endPage: entry.document.end_page,
      rotation: degrees,
    };

    try {
      const current = renderedPdf ?? (await getSourceBuffer(BASE_SOURCE_ID));
      showRendered(await rotatePdfPages(current, [turn]));
    } catch (error) {
      const failure = error instanceof Error ? error : new Error(String(error));
      setMergeError(failure.message);
      onMergeError?.(failure);
    }
  };

  const moveByOffset = (id: string, offset: number) => {
    const index = entries.findIndex((entry) => entry.id === id);
    const target = entries[index + offset];
    if (index < 0 || !target) return;
    moveDocument(id, target.id);
  };

  const addFiles = (files: File[]) => {
    const supported: File[] = [];
    const rejected: File[] = [];
    files.forEach((file) => {
      if (classifyUpload(file)) supported.push(file);
      else rejected.push(file);
    });

    if (rejected.length > 0) {
      const failure = new Error(
        `Unsupported file type: ${rejected
          .map((file) => file.name)
          .join(", ")}. Use a PDF, JPEG, or PNG.`
      );
      setMergeError(failure.message);
      onMergeError?.(failure);
    }

    if (supported.length === 0) return;

    const newEntries = supported.map((file) => {
      uploadCounterRef.current += 1;
      const id = `upload-${uploadCounterRef.current}`;
      uploadFilesRef.current.set(id, file);
      return {
        id,
        document: {
          doc_name: uploadedDocName ?? fileBaseName(file),
          doc_type: uploadedDocType ?? "",
          page_count: 0,
          start_page: 0,
          end_page: 0,
          merge_order: 0,
        },
        sourceId: id,
        pageIndices: [],
      };
    });
    const newIds = newEntries.map((entry) => entry.id);

    setEntries((current) => {
      const lastSelectedIndex = current.reduce(
        (lastIndex, entry, index) =>
          selectedIds.has(entry.id) ? index : lastIndex,
        -1
      );
      const lastUploadIndex = current.reduce(
        (lastIndex, entry, index) => (isUploadEntry(entry) ? index : lastIndex),
        -1
      );
      let insertAt =
        lastSelectedIndex >= 0 ? lastSelectedIndex + 1 : current.length;
      if (lastUploadIndex >= 0) {
        insertAt = Math.max(insertAt, lastUploadIndex + 1);
      }
      return [
        ...current.slice(0, insertAt),
        ...newEntries,
        ...current.slice(insertAt),
      ];
    });
    setPendingIds((current) => new Set([...current, ...newIds]));
    setPreviewUploadIds((current) => new Set([...current, ...newIds]));
    const focusId = newIds[newIds.length - 1];
    if (focusId) {
      setActiveId(focusId);
      focusEntryAfterMergeRef.current = focusId;
    }
    if (autoMergeOnUpload) {
      setAutoMergeRequest((current) => current + 1);
    }
    onAddDocument?.(supported);
  };

  const removeUpload = (id: string) => {
    const removed = entries.find((entry) => entry.id === id);
    const shouldRebuild =
      removed != null &&
      (isRenderedDocument(removed.document) || previewUploadIds.has(id));

    uploadFilesRef.current.delete(id);
    sourceBuffersRef.current.delete(id);
    setEntries((current) => current.filter((entry) => entry.id !== id));
    setSelectedIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    setPendingIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    setPreviewUploadIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    setActiveId((current) => {
      if (current !== id) return current;
      const remaining = entries.filter((entry) => entry.id !== id);
      const nextActive =
        remaining.find((entry) => isRenderedDocument(entry.document)) ??
        remaining[0];
      return nextActive?.id ?? "";
    });
    if (shouldRebuild) {
      setAutoMergeRequest((current) => current + 1);
    }
  };

  const getSourceBuffer = async (sourceId: string): Promise<ArrayBuffer> => {
    const buffers = sourceBuffersRef.current;
    const cached = buffers.get(sourceId);
    if (cached) return cached;

    const file = uploadFilesRef.current.get(sourceId);
    const buffer = file
      ? await fileToPdfBuffer(file)
      : await sourceToArrayBuffer(src);
    buffers.set(sourceId, buffer);
    return buffer;
  };

  const rebuildRenderedPdf = async (
    mergeEngine: MergeEngine
  ): Promise<{
    entries: DocumentEntry[];
    result: PdfMergeResult;
    mergeOrder: DocumentEntry[];
  }> => {
    const openedDocuments: OpenedDocument[] = [];
    const openedBySource = new Map<string, OpenedDocument>();

    const openSource = async (sourceId: string): Promise<OpenedDocument> => {
      const alreadyOpen = openedBySource.get(sourceId);
      if (alreadyOpen) return alreadyOpen;

      const content = await getSourceBuffer(sourceId);
      const opened = await mergeEngine
        .openDocumentBuffer({ id: `${sourceId}-${Date.now()}`, content })
        .toPromise();
      openedDocuments.push(opened);
      openedBySource.set(sourceId, opened);
      return opened;
    };

    try {
      const resolvedEntries = await Promise.all(
        entries.map(async (entry) => {
          if (!pendingIds.has(entry.id)) return entry;
          const opened = await openSource(entry.sourceId);
          return {
            ...entry,
            document: { ...entry.document, page_count: opened.pageCount },
            pageIndices: pageIndices(1, opened.pageCount),
          };
        })
      );

      const renderedIds = buildRenderedIds(
        resolvedEntries,
        selectedIds,
        previewUploadIds
      );
      const renderedEntries = resolvedEntries.filter((entry) =>
        renderedIds.has(entry.id)
      );

      const mergeConfigs = await Promise.all(
        renderedEntries.map(async (entry) => ({
          docId: (await openSource(entry.sourceId)).id,
          pageIndices: entry.pageIndices,
        }))
      );
      const mergedFile = await mergeEngine.mergePages(mergeConfigs).toPromise();

      const renumbered = renumberEntries(
        renderedFirst(resolvedEntries, renderedIds),
        renderedIds
      );

      const pdf = await rotatePdfPages(
        mergedFile.content,
        renumbered
          .filter((entry) => renderedIds.has(entry.id))
          .map((entry) => ({
            startPage: entry.document.start_page,
            endPage: entry.document.end_page,
            rotation: entry.document.rotation ?? 0,
          }))
      );

      return {
        entries: renumbered,
        mergeOrder: renderedEntries,
        result: {
          documents: toPublicDocuments(
            renumbered.map((entry) => entry.document)
          ),
          renderedDocuments: toPublicDocuments(
            renumbered
              .filter((entry) => renderedIds.has(entry.id))
              .map((entry) => entry.document)
          ),
          addedDocuments: toPublicDocuments(
            renumbered
              .filter((entry) => pendingIds.has(entry.id))
              .map((entry) => entry.document)
          ),
          pdf,
        },
      };
    } finally {
      openedDocuments.forEach((openedDocument) => {
        mergeEngine.closeDocument(openedDocument);
      });
    }
  };

  const runMerge = async (): Promise<{
    result: PdfMergeResult;
    renderVersion: number;
  } | null> => {
    if (activeMergeRequestRef.current != null) return null;
    if (!engine) {
      const publicDocs = toPublicDocuments(
        entries.map((entry) => entry.document)
      );
      onProceed?.({
        documents: publicDocs,
        renderedDocuments: publicDocs.filter(isRenderedDocument),
        addedDocuments: [],
        pdf: null,
      });
      return null;
    }

    const request = ++mergeRequestRef.current;
    activeMergeRequestRef.current = request;
    setIsMerging(true);
    setMergeError(null);
    try {
      const {
        entries: nextEntries,
        result,
        mergeOrder,
      } = await rebuildRenderedPdf(engine);
      if (request !== mergeRequestRef.current) return null;

      const focusId = focusEntryAfterMergeRef.current;
      focusEntryAfterMergeRef.current = null;
      const focusPage =
        focusId != null ? mergeOrderStartPage(mergeOrder, focusId) : null;

      if (focusId && focusPage != null) {
        setActiveId(focusId);
        setMergeFocusPage(focusPage);
      }

      setEntries(nextEntries);
      setPendingIds((current) => {
        const next = new Set(current);
        nextEntries.forEach((entry) => {
          if (entry.pageIndices.length > 0) next.delete(entry.id);
        });
        return next;
      });
      const completedVersion = result.pdf
        ? showRendered(result.pdf)
        : renderVersionRef.current;

      if (focusId && focusPage != null) {
        navigateViewerToPage(focusPage);
      } else if (!focusId) {
        setActiveId((current) => {
          const stillRendered = nextEntries.find(
            (entry) =>
              entry.id === current && isRenderedDocument(entry.document)
          );
          if (stillRendered) return current;
          const firstVisible = nextEntries.find((entry) =>
            isRenderedDocument(entry.document)
          );
          return firstVisible?.id ?? "";
        });
      }
      onDocumentsChange?.(result.documents);
      onProceed?.(result);
      return { result, renderVersion: completedVersion };
    } catch (error) {
      if (request !== mergeRequestRef.current) return null;
      const failure = error instanceof Error ? error : new Error(String(error));
      setMergeError(failure.message);
      onMergeError?.(failure);
      return null;
    } finally {
      if (request === mergeRequestRef.current) {
        activeMergeRequestRef.current = null;
        setIsMerging(false);
      }
    }
  };

  const handlePreview = async () => {
    return runMerge();
  };

  useEffect(() => {
    if (autoMergeRequest === 0) return;
    if (handledAutoMergeRef.current === autoMergeRequest) return;
    if (!engine || isMerging) return;

    handledAutoMergeRef.current = autoMergeRequest;
    void runMerge();
  }, [autoMergeRequest, engine, isMerging]);

  useEffect(() => {
    if (reorderRequest === 0 || step !== "preview") return;
    if (handledReorderRef.current === reorderRequest) return;
    if (!engine || isMerging) return;

    handledReorderRef.current = reorderRequest;
    void runMerge();
  }, [reorderRequest, step, engine, isMerging]);

  const trimmedMergedTitle = mergedTitle.trim();
  const savedFileName = trimmedMergedTitle
    ? documentFileName(trimmedMergedTitle)
    : downloadFileName;
  const showsPdfSuffix =
    trimmedMergedTitle !== "" && !/\.pdf$/i.test(trimmedMergedTitle);

  const firstRendered = entries.find((entry) =>
    isRenderedDocument(entry.document)
  );
  const activeEntry = entries.find((entry) => entry.id === activeId);
  const activeDocType = activeEntry?.document.doc_type ?? "";
  const activeDocTypeSelectOptions = docTypeSelectOptionsFor(
    activeDocType,
    docTypeChoices
  );
  const viewerStartPage =
    activeEntry && isRenderedDocument(activeEntry.document)
      ? activeEntry.document.start_page
      : firstRendered?.document.start_page;
  const viewerInitialPage =
    mergeFocusPage ?? pdfRendererProps?.initialPage ?? viewerStartPage;
  const rotateTarget =
    activeEntry && isRenderedDocument(activeEntry.document)
      ? activeEntry
      : firstRendered;
  const rotateTargetRotation = rotateTarget
    ? normalizeRotation(rotateTarget.document.rotation ?? 0)
    : 0;
  const rotateTitle = (degrees: number) => {
    const direction = degrees < 0 ? "counter-clockwise" : "clockwise";
    const suffix =
      rotateTargetRotation === 0 ? "" : ` (now ${rotateTargetRotation}°)`;
    return rotateTarget
      ? `Rotate ${rotateTarget.document.doc_name} 90° ${direction}${suffix}`
      : `Rotate 90° ${direction}`;
  };
  const viewerToolbar =
    pdfRendererProps?.toolbar !== undefined || !showRotate
      ? pdfRendererProps?.toolbar
      : (ctx: PdfToolbarContext) => (
          <PdfToolbar ctx={ctx}>
            {[-90, 90].map((degrees) => (
              <button
                key={degrees}
                type="button"
                className="pdf-renderer__tool-btn"
                aria-label={rotateTitle(degrees)}
                title={rotateTitle(degrees)}
                disabled={!rotateTarget}
                onClick={() => {
                  if (rotateTarget) void rotateDocument(rotateTarget, degrees);
                }}
              >
                {degrees < 0 ? (
                  <RotateCcw size={16} aria-hidden="true" />
                ) : (
                  <RotateCw size={16} aria-hidden="true" />
                )}
              </button>
            ))}
          </PdfToolbar>
        );
  const untypedUploads = entries.filter(needsDocType);
  const canPreview =
    !previewDisabled &&
    !isMerging &&
    engine != null &&
    untypedUploads.length === 0 &&
    selectedDocuments.length > 0;

  const statusMessage =
    mergeError ??
    (engineError
      ? `PDF engine failed to load: ${engineError.message}`
      : engineLoading
      ? "Preparing the PDF engine…"
      : untypedUploads.length > 0
      ? `Select a document type for ${untypedUploads
          .map((entry) => entry.document.doc_name)
          .join(", ")} to continue.`
      : null);

  const sidebarEntries = isPreviewStep
    ? entries.filter((entry) => isRenderedDocument(entry.document))
    : entries;

  return {
    entries,
    selectedIds,
    activeId,
    setActiveId,
    step,
    setStep,
    mergedTitle,
    setMergedTitle,
    isPreviewStep,
    isMerging,
    mergeError,
    renderedPdf,
    renderVersion,
    trimmedMergedTitle,
    savedFileName,
    showsPdfSuffix,
    activeSrc,
    pdfRef,
    fileInputRef,
    titleFieldId,
    rowRefs,
    sidebarEntries,
    statusMessage,
    canPreview,
    activeEntry,
    activeDocTypeSelectOptions,
    viewerInitialPage,
    viewerNavigatePage,
    viewerToolbar,
    pendingIds,
    activateDocument,
    toggleDocument,
    setDocType,
    moveDocument,
    moveByOffset,
    rotateDocument,
    addFiles,
    removeUpload,
    handlePreview,
    draggedIdRef,
    pendingViewerPageRef,
    flushViewerNavigation,
    applyPendingViewerNavigation,
    viewerLayoutReadyRef,
    onMergedTitleChange,
    pdfRendererProps,
  };
}

export type UsePdfMergeReturn = ReturnType<typeof usePdfMergeImpl>;
